import "./new.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  ingredientInputs,
  doughInputs,
  productInputs,
  userInputs,
  ingredientVolumeInputs,
  lineInputs,
  ovenInputs,
  productionListInputs,
} from "../../formSource";

import IngredientsForm from "./IngredientsForm";
import DoughsForm from "./DoughsForm";
import ProductsForm from "./ProductsForm";
import ProductionListsForm from "./ProductionListsForm.jsx";

import { MASS_UNITS, VOLUME_UNITS } from "../../constants";

const New = () => {
  const [file, setFile] = useState("");
  const [data, setData] = useState({});
  const [per, setPerc] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [doughs, setDoughs] = useState([]);
  const [products, setProducts] = useState([]);
  const [productionLines, setProductionLines] = useState([]);
  // RECIPE STATE
  const [ingredientAmounts, setIngredientAmounts] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedIngredientUnits, setSelectedIngredientUnits] = useState([]);
  const [conversionRates, setConversionRates] = useState([]);

  // MIX WEIGHT
  const [minMixUnit, setMinMixUnit] = useState(MASS_UNITS[0]);
  const [maxMixUnit, setMaxMixUnit] = useState(MASS_UNITS[0]);

  // UNITS
  const [ingredientUnits, setIngredientUnits] = useState([]);
  const [productUnits, setProductUnits] = useState([]);

  // LINES / PRODUCTS %
  const [productsPercentage, setProductsPercentage] = useState([]);

  // FLATTENED authoritative representation for productionlists
  // [{ lineId: "lines/<id>", productId: "products/<id>" }, ...]
  const [flattenedLinesAndProducts, setFlattenedLinesAndProducts] = useState(
    []
  );

  const flattenedRef = useRef(flattenedLinesAndProducts);
  useEffect(() => {
    flattenedRef.current = flattenedLinesAndProducts;
  }, [flattenedLinesAndProducts]);

  const navigate = useNavigate();
  const { type } = useParams();
  const organizationId = JSON.parse(localStorage.getItem("organizations"))
    .selected.id;

  const formConfig = {
    ingredients: { inputs: ingredientInputs, title: "Add New Ingredient Form" },
    doughs: { inputs: doughInputs, title: "Add New Doughs Form" },
    products: { inputs: productInputs, title: "Add New Product Form" },
    users: { inputs: userInputs, title: "Add New User Form" },
    lines: { inputs: lineInputs, title: "Add New Production Line Form" },
    productionlists: {
      inputs: productionListInputs,
      title: "Add New Production List Form",
    },
    ovens: { inputs: ovenInputs, title: "Add New Oven Form" },
  };

  const toNumberOrZero = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const fetchItems = (collectionName, stateUpdater) => {
    return onSnapshot(
      collection(db, "organizations", organizationId, collectionName),
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({
          id: d.id,
          collection: collectionName,
          ...d.data(),
        }));
        stateUpdater(data);
      },
      (error) => {
        console.log(error);
      }
    );
  };

  useEffect(() => {
    const unsubIngredients = fetchItems("ingredients", setIngredients);
    const unsubDoughs = fetchItems("doughs", setDoughs);
    const unsubProducts = fetchItems("products", setProducts);
    const unsubLines = fetchItems("lines", setProductionLines);
    return () => {
      unsubIngredients();
      unsubDoughs();
      unsubProducts();
      unsubLines();
    };
  }, [organizationId]);

  const sortedIngredients = [...ingredients].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const sortedDoughs = [...doughs].sort((a, b) => a.name.localeCompare(b.name));
  const sortedProducts = [...products].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const sortedLines = [...productionLines].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const allItems = [...sortedIngredients, ...sortedDoughs, ...sortedProducts];

  const handleInput = (e) => {
    const { id, value, type: t, checked } = e.target;
    setData({
      ...data,
      [id]: t === "checkbox" ? checked : t === "number" ? parseFloat(value) || 0 : value,
    });
  };

  const handleSelectChange = (e, index, stateUpdater) => {
    const { value, dataset } = e.target;
    let newIngredientAmounts;
    let defaultUnit;
    let newSelectedUnits;
    switch (dataset.key) {
      case "ingredients": {
        const selectedIngredient = allItems.find((ing) => ing.id === value);
        newIngredientAmounts = [...stateUpdater];
        newIngredientAmounts[index] = {
          id: value,
          amount: "",
          collection: selectedIngredient.collection,
          selected: true,
        };
        setIngredientAmounts(newIngredientAmounts);

        const isVolume = selectedIngredient.volume ? selectedIngredient.volume : false;
        if (isVolume) {
          const newConversionRates = [...conversionRates];
          newConversionRates[index] = selectedIngredient.massconversion;
          setConversionRates(newConversionRates);
        }
        defaultUnit = isVolume ? VOLUME_UNITS[0] : MASS_UNITS[0];
        newSelectedUnits = [...selectedUnits];
        newSelectedUnits[index] = { unit: MASS_UNITS[0], isVolume: false };
        setSelectedUnits(newSelectedUnits);
        break;
      }
      case "doughs": {
        const selectedDough = allItems.find((d) => d.id === value);
        newIngredientAmounts = [...stateUpdater];
        newIngredientAmounts[index] = {
          id: value,
          amount: "",
          collection: selectedDough.collection,
          selected: true,
        };
        setIngredientAmounts(newIngredientAmounts);

        defaultUnit = MASS_UNITS[0];
        newSelectedUnits = [...selectedUnits];
        newSelectedUnits[index] = { unit: MASS_UNITS[0], isVolume: false };
        setSelectedUnits(newSelectedUnits);
        break;
      }
      default:
        break;
    }
  };

  const handleAddIngredient = () => {
    setIngredientAmounts([...ingredientAmounts, { id: "", amount: "", collection: "" }]);
  };

  const handleAddIngredientForProducts = (type) => {
    const newKey = type === "doughs" ? `doughs-${Date.now()}` : `ingredients-${Date.now()}`;
    setIngredientAmounts([...ingredientAmounts, { id: newKey, amount: "", collection: type, selected: false }]);
    setSelectedUnits([...selectedUnits, { isVolume: false, unit: MASS_UNITS[0] }]);
  };

  const handleRemoveItem = (index) => {
    const newIngredientAmounts = [...ingredientAmounts];
    newIngredientAmounts.splice(index, 1);
    setIngredientAmounts(newIngredientAmounts);
    const newSelectedUnits = [...selectedUnits];
    newSelectedUnits.splice(index, 1);
    setSelectedUnits(newSelectedUnits);
    const newConversionRates = [...conversionRates];
    newConversionRates.splice(index, 1);
    setConversionRates(newConversionRates);
  };

  const handleUnitChange = (e, index) => {
    const { value } = e.target;
    let isVolume;
    let unit = VOLUME_UNITS.find((u) => u.abbreviation === value);
    if (!unit) {
      unit = MASS_UNITS.find((u) => u.abbreviation === value);
      isVolume = false;
    } else {
      isVolume = true;
    }
    if (unit) {
      const newSelectedUnits = [...selectedUnits];
      newSelectedUnits[index] = { unit, isVolume };
      setSelectedUnits(newSelectedUnits);
    }
  };

  const handleMixUnitChange = (e) => {
    const { value, id } = e.target;
    const unit = MASS_UNITS.find((u) => u.abbreviation === value);
    if (id === "minMix") setMinMixUnit(unit);
    else setMaxMixUnit(unit);
  };

  const handleIngredientWeightUnitChange = (e, index) => {
    const { value } = e.target;
    let unit = VOLUME_UNITS.find((u) => u.abbreviation === value);
    let isVolume = !!unit;
    if (!unit) unit = MASS_UNITS.find((u) => u.abbreviation === value);
    if (unit) {
      const newSelectedIngredientUnits = [...selectedIngredientUnits];
      newSelectedIngredientUnits[index] = unit;
      setSelectedIngredientUnits(newSelectedIngredientUnits);
    }
  };

  const handleMixInput = (e) => {
    let mixValue = 0;
    const { id, value } = e.target;
    if (id === "minMix") mixValue = value * minMixUnit.amount;
    else mixValue = value * maxMixUnit.amount;
    setData({ ...data, [id]: mixValue });
  };

  const handleAddProduct = () => {
    setProductsPercentage([
      ...productsPercentage,
      {
        id: "",
        scrapPercent: "",
        piecesPerHour: "",
        capacity: "",
        bakeTimeMinutes: "",
        preheatMinutes: "",
        cooldownMinutes: "",
      },
    ]);
  };

  const handleRemoveProduct = (index) => {
    const newProductsPercentage = [...productsPercentage];
    newProductsPercentage.splice(index, 1);
    setProductsPercentage(newProductsPercentage);
  };

  const handleProductUnitChange = (e, index, field) => {
    const newUnits = [...productUnits];
    newUnits[index][field] = e.target.value;
    setProductUnits(newUnits);
  };

  const handleIngredientUnitChange = (e, index, field) => {
    const newUnits = [...ingredientUnits];
    let amountValue = 1;
    if (field === "unitWeight") {
      let selectedUnit = MASS_UNITS.find(
        (unit) => unit.abbreviation === selectedIngredientUnits[index].abbreviation
      );
      if (selectedUnit) {
        amountValue = selectedUnit ? selectedUnit.amount : 1;
        newUnits[index][field] = e.target.value * amountValue;
      } else {
        selectedUnit = VOLUME_UNITS.find(
          (u) => u.abbreviation === selectedIngredientUnits[index].abbreviation
        );
        amountValue = selectedUnit ? selectedUnit.amount : 1;
        newUnits[index][field] = e.target.value * data.massconversion * selectedIngredientUnits[index].amount;
      }
    } else {
      newUnits[index][field] = e.target.value;
    }
    setIngredientUnits(newUnits);
  };

  const handleAddUnit = () => {
    if (type === "products") setProductUnits([...productUnits, { name: "", pieceCount: "" }]);
    if (type === "ingredients") {
      setIngredientUnits([...ingredientUnits, { name: "", unitWeight: "" }]);
      const defaultUnit = data.isVolume ? VOLUME_UNITS[0] : MASS_UNITS[0];
      setSelectedIngredientUnits([...selectedIngredientUnits, defaultUnit]);
    }
  };

  const handleRemoveUnit = (index) => {
    if (type === "products") {
      const newUnits = [...productUnits];
      newUnits.splice(index, 1);
      setProductUnits(newUnits);
    }
    if (type === "ingredients") {
      const newUnits = [...ingredientUnits];
      const newSelected = [...selectedIngredientUnits];
      newUnits.splice(index, 1);
      newSelected.splice(index, 1);
      setIngredientUnits(newUnits);
      setSelectedIngredientUnits(newSelected);
    }
  };

  const handleProductSelectChange = (e, index) => {
    const { value } = e.target;
    const newProductsPercentage = [...productsPercentage];
    newProductsPercentage[index].id = value;
    setProductsPercentage(newProductsPercentage);
  };

  const handleProductPercentageChange = (e, index) => {
    const { name, value } = e.target;
    const newProductsPercentage = [...productsPercentage];
    newProductsPercentage[index][name] = value;
    setProductsPercentage(newProductsPercentage);
  };

  function getItemNameByProductRef(productRef) {
    const [collection, id] = String(productRef).split("/");
    const item = allItems.find((it) => it.collection === collection && it.id === id);
    return item ? item.name : "Item not found";
  }

  // Helper utilities for flat-first editor
  function getIdFromRef(ref) {
    if (!ref) return "";
    const parts = String(ref).split("/");
    return parts.length > 1 ? parts[1] : ref;
  }

  function makeLineRef(idOrRef) {
    if (!idOrRef) return "";
    if (String(idOrRef).startsWith("lines/")) return idOrRef;
    return `lines/${idOrRef}`;
  }

  function makeProductRef(idOrRef) {
    if (!idOrRef) return "";
    if (String(idOrRef).startsWith("products/")) return idOrRef;
    return `products/${idOrRef}`;
  }

  // Row operations (flat)
  const addBlankPair = () => {
    setFlattenedLinesAndProducts((prev) => [...prev, { lineId: "", productId: "" }]);
  };

  const updatePair = (index, updates) => {
    setFlattenedLinesAndProducts((prev) => {
      const copy = prev.slice();
      copy[index] = { ...copy[index], ...updates };
      if (updates.lineId) {
        const lineIdPlain = getIdFromRef(updates.lineId);
        const lineObj = productionLines.find((l) => l.id === lineIdPlain);
        if (lineObj) {
          const productRefs = (lineObj.products || []).map((p) => Object.keys(p)[0]);
          if (!productRefs.includes(copy[index].productId)) {
            copy[index].productId = "";
          }
        }
      }
      return copy;
    });
  };

  const removePair = (index) => {
    setFlattenedLinesAndProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const movePair = (fromIndex, toIndex) => {
    setFlattenedLinesAndProducts((prev) => {
      const copy = prev.slice();
      const [item] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, item);
      return copy;
    });
  };

  // Append all products for a given line (avoid duplicates)
  const addAllProductsForLine = (lineIdRef, insertAt = null) => {
    const lineIdPlain = getIdFromRef(lineIdRef);
    const lineObj = productionLines.find((l) => l.id === lineIdPlain);
    if (!lineObj || !Array.isArray(lineObj.products)) return;

    const toAppend = lineObj.products.map((p) => Object.keys(p)[0]); // productRef strings
    setFlattenedLinesAndProducts((prev) => {
      const existingSet = new Set(prev.map((p) => `${p.lineId}|${p.productId}`));
      const newPairs = [];
      toAppend.forEach((prodRef) => {
        const key = `${makeLineRef(lineIdPlain)}|${prodRef}`;
        if (!existingSet.has(key)) {
          newPairs.push({ lineId: makeLineRef(lineIdPlain), productId: prodRef });
          existingSet.add(key);
        }
      });
      if (newPairs.length === 0) return prev;
      if (insertAt === null || insertAt < 0 || insertAt >= prev.length) {
        return [...prev, ...newPairs];
      } else {
        const copy = prev.slice();
        copy.splice(insertAt + 1, 0, ...newPairs);
        return copy;
      }
    });
  };

  // Utility: products for a specific line
  function productsForLine(lineIdRef) {
    const id = getIdFromRef(lineIdRef);
    const line = productionLines.find((l) => l.id === id);
    if (!line || !Array.isArray(line.products)) return [];
    return line.products.map((p) => Object.keys(p)[0]); // productRef
  }

  // Used-product set excluding an index (so that current row can keep its selection)
  function usedProductSetExcludingIndex(excludeIndex = null) {
    const s = new Set();
    flattenedLinesAndProducts.forEach((p, i) => {
      if (!p) return;
      if (i === excludeIndex) return;
      if (p.productId) s.add(p.productId);
    });
    return s;
  }

  function availableProductsForLine(lineIdRef, rowIndex = null) {
    const prods = productsForLine(lineIdRef);
    if (!prods || prods.length === 0) return [];
    const used = usedProductSetExcludingIndex(rowIndex);
    return prods.filter((prodRef) => !used.has(prodRef));
  }

  function isLineSelectable(lineIdRef, rowIndex = null) {
    const avail = availableProductsForLine(lineIdRef, rowIndex);
    return avail.length > 0;
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      let newData = {};

      if (type === "ingredients" || type === "doughs" || type === "products") {
        newData = {
          ...data,
          ingredients: ingredientAmounts.map(({ id, amount, collection }) => ({
            [`${collection}/${id}`]: amount,
          })),
          ...(type === "ingredients" && {
            ingredientUnits: ingredientUnits.map(({ name, unitWeight }) => ({
              [name]: unitWeight,
            })),
          }),
          ...(type === "products" && { productUnits }),
        };
      } else if (type === "lines") {
        newData = {
          ...data,
          products: productsPercentage.map((product) => ({
            [`products/${product.id}`]: {
              piecesPerHour: product.piecesPerHour,
              scrapPercent: product.scrapPercent,
            },
          })),
        };
      } else if (type === "ovens") {
        const normalizedProducts = productsPercentage
          .filter((product) => product.id)
          .map((product) => ({
            [`products/${product.id}`]: {
              piecesPerHour: toNumberOrZero(product.piecesPerHour),
              capacity: toNumberOrZero(product.capacity),
              bakeTimeMinutes: toNumberOrZero(product.bakeTimeMinutes),
              preheatMinutes: toNumberOrZero(product.preheatMinutes),
              cooldownMinutes: toNumberOrZero(product.cooldownMinutes),
            },
          }));

        newData = {
          ...data,
          capacity: toNumberOrZero(data.capacity),
          bakeTimeMinutes: toNumberOrZero(data.bakeTimeMinutes),
          preheatMinutes: toNumberOrZero(data.preheatMinutes),
          cooldownMinutes: toNumberOrZero(data.cooldownMinutes),
          throughputPerHour: toNumberOrZero(data.throughputPerHour),
          products: normalizedProducts,
        };
      } else if (type === "productionlists") {
        newData = {
          ...data,
          linesAndProducts: flattenedLinesAndProducts.map((p) => ({
            lineId: p.lineId,
            productId: p.productId,
          })),
        };
      } else {
        newData = {
          ...data,
        };
      }

      if (type === "users") {
        const res = await createUserWithEmailAndPassword(auth, data.email, data.password);
        await setDoc(doc(db, "users", res.user.uid), {
          ...data,
          timeStamp: serverTimestamp(),
        });
      } else {
        const newItemRef = doc(collection(db, "organizations", organizationId, type));
        await setDoc(newItemRef, newData);
      }

      navigate(-1);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="new">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top">
          <h1>{formConfig[type]?.title}</h1>
        </div>
        <div className="bottom">
          <div className="left">
            <img
              src={
                file
                  ? URL.createObjectURL(file)
                  : "https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg"
              }
              alt=""
            />
          </div>
          <div className="right">
            <form onSubmit={handleAdd}>
              <div className="formInput">
                <label htmlFor="file">
                  Image: <DriveFolderUploadOutlinedIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  style={{ display: "none" }}
                />
              </div>

              {formConfig[type]?.inputs.map((input) => (
                <div className="formInput" key={input.id}>
                  <label>{input.label}</label>
                  <input id={input.id} type={input.type} placeholder={input.placeholder} onChange={handleInput} />
                </div>
              ))}

              {type === "ingredients" && (
                <IngredientsForm
                  data={data}
                  ingredientAmounts={ingredientAmounts}
                  handleSelectChange={handleSelectChange}
                  allItems={sortedIngredients}
                  selectedUnits={selectedUnits}
                  conversionRates={conversionRates}
                  setIngredientAmounts={setIngredientAmounts}
                  handleUnitChange={handleUnitChange}
                  handleRemoveItem={handleRemoveItem}
                  handleAddIngredient={handleAddIngredient}
                  ingredientUnits={ingredientUnits}
                  handleIngredientUnitChange={handleIngredientUnitChange}
                  handleIngredientWeightUnitChange={handleIngredientWeightUnitChange}
                  handleRemoveUnit={handleRemoveUnit}
                  handleAddUnit={handleAddUnit}
                  ingredientVolumeInputs={ingredientVolumeInputs}
                  handleInput={handleInput}
                  selectedIngredientUnits={selectedIngredientUnits}
                  setIngredientUnits={setIngredientUnits}
                />
              )}

              {type === "doughs" && (
                <DoughsForm
                  handleMixInput={handleMixInput}
                  handleMixUnitChange={handleMixUnitChange}
                  ingredientAmounts={ingredientAmounts}
                  handleSelectChange={handleSelectChange}
                  allItems={[...sortedIngredients, ...sortedDoughs]}
                  selectedUnits={selectedUnits}
                  conversionRates={conversionRates}
                  setIngredientAmounts={setIngredientAmounts}
                  handleUnitChange={handleUnitChange}
                  handleRemoveItem={handleRemoveItem}
                  handleAddIngredient={handleAddIngredient}
                />
              )}

              {type === "products" && (
                <ProductsForm
                  ingredientAmounts={ingredientAmounts}
                  handleSelectChange={handleSelectChange}
                  allItems={[
                    [
                      ...sortedIngredients,
                      ...sortedDoughs,
                      ...sortedProducts,
                    ],
                  ]}
                  selectedUnits={selectedUnits}
                  conversionRates={conversionRates}
                  setIngredientAmounts={setIngredientAmounts}
                  handleUnitChange={handleUnitChange}
                  handleRemoveItem={handleRemoveItem}
                  handleAddIngredient={handleAddIngredient}
                  handleAddUnit={handleAddUnit}
                  productUnits={productUnits}
                  handleProductUnitChange={handleProductUnitChange}
                  handleRemoveUnit={handleRemoveUnit}
                  doughs={sortedDoughs}
                  ingredients={sortedIngredients}
                  handleAddIngredientForProducts={handleAddIngredientForProducts}
                />
              )}

              {type === "lines" && (
                <div>
                  <h3>Products run on line:</h3>
                  {productsPercentage.length > 0 && (
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Pieces/Hour</th>
                          <th>Scrap %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productsPercentage.map((product, index) => {
                          const selectedIds = productsPercentage.map((p) => p.id).filter((id, i) => i !== index);
                          const availableProducts = sortedProducts.filter((p) => !selectedIds.includes(p.id));
                          return (
                            <tr key={index}>
                              <td>
                                <select onChange={(e) => handleProductSelectChange(e, index)} value={product.id}>
                                  <option value="">Select Product</option>
                                  {availableProducts.map((p, idx) => (
                                    <option key={idx} value={p.id}>
                                      {p.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {product.id !== "" && (
                                <>
                                  <td>
                                    <input
                                      type="number"
                                      name="piecesPerHour"
                                      value={product.piecesPerHour}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Pieces/Hour"
                                      required
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      name="scrapPercent"
                                      value={product.scrapPercent}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Scrap Percentage"
                                    />
                                  </td>
                                </>
                              )}
                              <td>
                                <button type="button" onClick={() => handleRemoveProduct(index)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <button type="button" onClick={handleAddProduct}>
                    Add Product
                  </button>
                </div>
              )}

              {type === "ovens" && (
                <div>
                  <h3>Products run on oven:</h3>
                  {productsPercentage.length > 0 && (
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Pieces/Hour</th>
                          <th>Capacity/Batch</th>
                          <th>Bake Time (min)</th>
                          <th>Preheat (min)</th>
                          <th>Cooldown (min)</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productsPercentage.map((product, index) => {
                          const selectedIds = productsPercentage
                            .map((p) => p.id)
                            .filter((id, i) => i !== index);
                          const availableProducts = sortedProducts.filter(
                            (p) => !selectedIds.includes(p.id)
                          );
                          return (
                            <tr key={index}>
                              <td>
                                <select onChange={(e) => handleProductSelectChange(e, index)} value={product.id}>
                                  <option value="">Select Product</option>
                                  {availableProducts.map((p, i) => (
                                    <option value={p.id} key={i}>
                                      {p.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {product.id !== "" && (
                                <>
                                  <td>
                                    <input
                                      type="number"
                                      name="piecesPerHour"
                                      min={0}
                                      value={product.piecesPerHour}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Pieces/Hour"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      name="capacity"
                                      min={0}
                                      value={product.capacity}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Pieces/Batch"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      name="bakeTimeMinutes"
                                      min={0}
                                      value={product.bakeTimeMinutes}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Bake Minutes"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      name="preheatMinutes"
                                      min={0}
                                      value={product.preheatMinutes}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Preheat"
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      name="cooldownMinutes"
                                      min={0}
                                      value={product.cooldownMinutes}
                                      onChange={(e) => handleProductPercentageChange(e, index)}
                                      placeholder="Cooldown"
                                    />
                                  </td>
                                </>
                              )}
                              <td>
                                <button type="button" onClick={() => handleRemoveProduct(index)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <button type="button" onClick={handleAddProduct}>
                    Add Product
                  </button>
                </div>
              )}

              {type === "productionlists" && (
                <ProductionListsForm
                  flattenedLinesAndProducts={flattenedLinesAndProducts}
                  onAddBlankPair={addBlankPair}
                  onUpdatePair={updatePair}
                  onRemovePair={removePair}
                  onMovePair={movePair}
                  onAddAllProductsForLine={addAllProductsForLine}
                  availableProductsForLine={availableProductsForLine}
                  isLineSelectable={isLineSelectable}
                  sortedLines={sortedLines}
                  productionLines={productionLines}
                  getItemNameByProductRef={getItemNameByProductRef}
                />
              )}

              <div>
                <button disabled={per !== null && per < 100} type="submit">
                  Send
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default New;
